import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Building2, Plus, Search, Loader2, Edit3, Trash2, UserPlus, X, Check, Sparkles, Info, AlertTriangle, RotateCcw, Tag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import TagInput from '../components/TagInput';
import DataTable from '../components/table/DataTable';
import ViewSelector from '../components/table/ViewSelector';
import AddColumnPanel from '../components/table/AddColumnPanel';
import { COMPANY_FIELD_REGISTRY, DEFAULT_COMPANY_COLUMNS, buildVisibleColumns, getAllFields } from '../components/table/columnRegistry';
import useTableView from '../hooks/useTableView';
import useCustomFields from '../hooks/useCustomFields';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
    // Technology & Software
    'Computer Software', 'Information Technology & Services', 'Internet', 'Computer Hardware',
    'Semiconductors', 'Telecommunications', 'Computer Networking', 'Cybersecurity',
    'Artificial Intelligence', 'Cloud Computing', 'SaaS',
    // Financial
    'Financial Services', 'Banking', 'Insurance', 'Investment Management', 'Venture Capital & Private Equity',
    'Accounting', 'Capital Markets',
    // Healthcare & Life Sciences
    'Healthcare', 'Hospital & Health Care', 'Medical Devices', 'Pharmaceuticals', 'Biotechnology',
    'Health, Wellness & Fitness', 'Mental Health Care',
    // Professional Services
    'Management Consulting', 'Legal Services', 'Human Resources', 'Staffing & Recruiting',
    'Marketing & Advertising', 'Public Relations', 'Design', 'Architecture & Planning',
    // Industrial & Manufacturing
    'Manufacturing', 'Automotive', 'Aviation & Aerospace', 'Chemicals', 'Construction',
    'Electrical & Electronic Manufacturing', 'Mechanical & Industrial Engineering', 'Mining & Metals',
    // Consumer & Retail
    'Retail', 'Consumer Goods', 'Consumer Electronics', 'Food & Beverages', 'Restaurants',
    'Hospitality', 'Apparel & Fashion', 'Luxury Goods & Jewelry', 'Sporting Goods',
    // Media & Entertainment
    'Media', 'Entertainment', 'Music', 'Publishing', 'Online Media', 'Broadcasting',
    'Gaming', 'Film & Motion Pictures',
    // Education
    'Education', 'Higher Education', 'E-Learning', 'Research',
    // Energy & Environment
    'Energy', 'Oil & Gas', 'Renewables & Environment', 'Utilities',
    // Real Estate & Property
    'Real Estate', 'Commercial Real Estate', 'Property Management',
    // Transportation & Logistics
    'Transportation', 'Logistics & Supply Chain', 'Maritime', 'Warehousing',
    // Government & Nonprofit
    'Government Administration', 'Nonprofit Organization Management', 'Civic & Social Organization',
    'International Affairs', 'Defense & Space',
    // Agriculture
    'Agriculture', 'Farming', 'Food Production',
    // Other
    'Other',
];
const SIZES = ['startup', 'smb', 'mid-market', 'enterprise'];
// Canonical Investor Type options — keep in sync with backend
// models/account.py INVESTOR_TYPES and data_import.py ACCOUNT_FIELDS.
const INVESTOR_TYPES = ['Angel', 'Venture Capital', 'Private Equity', 'Family Office', 'Institutional'];
const EMPLOYEE_RANGES = [
    { range: '1-10',         category: 'Rabbits',   size: 'SMB',        color: 'bg-emerald-50 text-emerald-700' },
    { range: '11-20',        category: 'Rabbits',   size: 'SMB',        color: 'bg-emerald-50 text-emerald-700' },
    { range: '21-50',        category: 'Rabbits',   size: 'SMB',        color: 'bg-emerald-50 text-emerald-700' },
    { range: '51-100',       category: 'Deers',     size: 'SMB',        color: 'bg-blue-50 text-blue-700' },
    { range: '101-200',      category: 'Deers',     size: 'SMB',        color: 'bg-blue-50 text-blue-700' },
    { range: '201-500',      category: 'Elephants', size: 'Mid-Market', color: 'bg-violet-50 text-violet-700' },
    { range: '501-1,000',    category: 'Elephants', size: 'Mid-Market', color: 'bg-violet-50 text-violet-700' },
    { range: '1,001-2,000',  category: 'Elephants', size: 'Enterprise', color: 'bg-indigo-50 text-indigo-700' },
    { range: '2,001-5,000',  category: 'Whales',    size: 'Enterprise', color: 'bg-amber-50 text-amber-700' },
    { range: '5,001-10,000', category: 'Whales',    size: 'Enterprise', color: 'bg-amber-50 text-amber-700' },
    { range: '10,000+',      category: 'Whales',    size: 'Enterprise', color: 'bg-amber-50 text-amber-700' },
];
const STATUSES = [
    { value: 'target', label: 'Target', color: 'bg-slate-100 text-slate-700 border-slate-300', desc: 'Identified as a potential fit but no outreach yet. Matches your ICP criteria.' },
    { value: 'lead', label: 'Lead', color: 'bg-blue-50 text-blue-700 border-blue-300', desc: 'Initial engagement started — inbound inquiry, downloaded content, or first outreach sent.' },
    { value: 'prospect', label: 'Prospect', color: 'bg-indigo-50 text-indigo-700 border-indigo-300', desc: 'Qualified and actively being worked. A meeting has been held or discovery is underway.' },
    { value: 'opportunity', label: 'Opportunity', color: 'bg-violet-50 text-violet-700 border-violet-300', desc: 'Active deal in pipeline. Proposal sent, negotiations in progress, or in evaluation stage.' },
    { value: 'customer', label: 'Customer', color: 'bg-emerald-50 text-emerald-700 border-emerald-300', desc: 'Signed deal, active paying customer. Onboarding complete or in progress.' },
    { value: 'churned', label: 'Churned', color: 'bg-red-50 text-red-700 border-red-300', desc: 'Former customer who cancelled or did not renew. May be a candidate for win-back campaigns. Track reason for churn and last activity date.' },
    { value: 'partner', label: 'Partner', color: 'bg-stone-900 text-white border-stone-900', desc: 'Strategic partner, referral source, reseller, or integration partner. Not a direct customer but a business relationship. Track partnership type and joint revenue.' },
];
const STATUS_VALUES = STATUSES.map(s => s.value);

// ─── Status Selector with Info ─────────────────────────────────────────────

function StatusSelector({ value, onChange }) {
    const [showInfo, setShowInfo] = useState(null);

    return (
        <div>
            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Status</label>
            <div className="flex flex-wrap gap-1.5">
                {STATUSES.map(s => (
                    <div key={s.value} className="relative">
                        <div className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all cursor-pointer ${
                                value === s.value
                                    ? `${s.color} ring-2 ring-offset-1 ring-current`
                                    : 'bg-white border-stone-200 text-stone-500 hover:border-stone-300'
                            }`}
                            onClick={() => onChange(s.value)}>
                            {s.label}
                            <span onClick={(e) => { e.stopPropagation(); setShowInfo(showInfo === s.value ? null : s.value); }}
                                className="opacity-40 hover:opacity-100 cursor-help ml-0.5">
                                <Info className="w-3 h-3" />
                            </span>
                        </div>
                        {showInfo === s.value && (
                            <div className="absolute z-20 bottom-full mb-2 left-0 w-64 bg-white rounded-lg shadow-xl border border-stone-200 p-3">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${s.color}`}>{s.label}</span>
                                    <span onClick={() => setShowInfo(null)} className="text-stone-400 hover:text-stone-600 cursor-pointer text-xs">✕</span>
                                </div>
                                <p className="text-xs text-stone-600 leading-relaxed">{s.desc}</p>
                                <div className="absolute -bottom-1.5 left-4 w-3 h-3 bg-white border-r border-b border-stone-200 rotate-45" />
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── Industry Multi-Select ─────────────────────────────────────────────────

function IndustryMultiSelect({ value = [], onChange }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [customInput, setCustomInput] = useState('');

    const filtered = INDUSTRIES.filter(i =>
        i.toLowerCase().includes(search.toLowerCase()) && !value.includes(i)
    );

    const add = (industry) => { onChange([...value, industry]); };
    const remove = (industry) => { onChange(value.filter(v => v !== industry)); };
    const addCustom = () => {
        const v = customInput.trim();
        if (v && !value.includes(v)) { onChange([...value, v]); }
        setCustomInput('');
    };

    return (
        <div>
            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Industry</label>
            {/* Selected tags */}
            <div className="flex flex-wrap gap-1 mb-1.5 min-h-[28px]">
                {value.map(v => (
                    <span key={v} className="inline-flex items-center gap-1 bg-stone-100 text-stone-700 text-xs px-2 py-0.5 rounded-full border border-stone-200">
                        {v}
                        <button onClick={() => remove(v)} className="text-stone-400 hover:text-red-500 ml-0.5">
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
                {value.length === 0 && <span className="text-xs text-stone-400 py-0.5">No industries selected</span>}
            </div>
            {/* Dropdown trigger */}
            <button type="button" onClick={() => setOpen(!open)}
                className="w-full px-3 py-2 text-sm text-left border border-stone-200 rounded-lg bg-white hover:bg-stone-50 text-stone-500 focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                {open ? 'Close' : '+ Add industry...'}
            </button>
            {/* Dropdown */}
            {open && (
                <div className="mt-1 border border-stone-200 rounded-lg bg-white shadow-lg max-h-48 overflow-hidden">
                    <div className="p-2 border-b border-stone-100">
                        <input value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Search industries..." autoFocus
                            className="w-full px-2 py-1.5 text-xs border border-stone-200 rounded bg-stone-50 focus:outline-none" />
                    </div>
                    <div className="max-h-32 overflow-y-auto">
                        {filtered.slice(0, 20).map(i => (
                            <button key={i} type="button" onClick={() => { add(i); setSearch(''); }}
                                className="w-full px-3 py-1.5 text-xs text-left text-stone-700 hover:bg-stone-50 truncate">
                                {i}
                            </button>
                        ))}
                        {filtered.length > 20 && <p className="px-3 py-1 text-[10px] text-stone-400">Type to narrow down...</p>}
                        {filtered.length === 0 && search && (
                            <div className="px-3 py-2">
                                <p className="text-[10px] text-stone-400 mb-1">No match found.</p>
                                <div className="flex gap-1">
                                    <input value={customInput || search} onChange={e => setCustomInput(e.target.value)}
                                        className="flex-1 px-2 py-1 text-xs border border-stone-200 rounded" placeholder="Custom industry..." />
                                    <button type="button" onClick={() => { add(customInput || search); setCustomInput(''); setSearch(''); }}
                                        className="px-2 py-1 text-xs bg-stone-900 text-white rounded">Add</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Company Modal ──────────────────────────────────────────────────────────

function CompanyModal({ open, onClose, account, onSave }) {
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    // Empty-form defaults — keep in sync across the `useState` initialiser and
    // the `useEffect` reset path so new fields don't stay undefined on add.
    const EMPTY_FORM = {
        name: '', domain: '', industry: '', size: 'smb', annual_revenue: '', num_employees: '',
        website: '', phone: '', email: '',
        address: '', city: '', state: '', country: 'US', region: '', hq_country: '',
        description: '', status: 'prospect',
        linkedin_url: '', twitter_url: '', founded_year: '', funding_raised: '', employee_range: '',
        lead_investor: '', investor_type: '',
    };
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(false);
    const [enriching, setEnriching] = useState(false);
    const [enrichSteps, setEnrichSteps] = useState([]);
    const [duplicate, setDuplicate] = useState(null); // check-duplicate payload
    const [claiming, setClaiming] = useState(false);

    useEffect(() => {
        if (account) setForm({ ...EMPTY_FORM, ...account, tags: account.tags || [] });
        else setForm(EMPTY_FORM);
        setDuplicate(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account]);

    // Duplicate gate (new companies only): the same company URL must never be
    // added twice. Allocated → blocked; unallocated → offer takeover.
    const checkDuplicate = async (url) => {
        if (account) return null; // editing an existing company
        try {
            const res = await axios.post(`${API}/accounts/check-duplicate`,
                { url }, { headers: getAuthHeader() });
            if (res.data?.exists) {
                setDuplicate(res.data);
                return res.data;
            }
            setDuplicate(null);
            return null;
        } catch {
            return null; // check is best-effort; the create endpoint still enforces
        }
    };

    const handleClaim = async () => {
        if (!duplicate?.account_id) return;
        setClaiming(true);
        try {
            const res = await axios.post(`${API}/accounts/${duplicate.account_id}/claim`,
                {}, { headers: getAuthHeader() });
            toast.success(`You now own ${duplicate.name}`);
            onSave(res.data);
            onClose();
            navigate(`/companies/${duplicate.account_id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to take ownership');
        } finally {
            setClaiming(false);
        }
    };

    const handleEnrich = async () => {
        let url = (form.website || '').trim();
        if (!url) return toast.error('Enter a website URL first');
        if (!url.startsWith('http')) url = `https://${url}`;

        // Check before spending AI credits — and before the coach invests time.
        const dup = await checkDuplicate(url);
        if (dup) {
            if (dup.allocated) return; // banner explains; can't proceed
            // Unallocated: banner offers takeover; don't enrich a duplicate.
            return;
        }

        setEnriching(true);
        setEnrichSteps([{ text: 'Connecting to AI research agent...', done: false }]);

        try {
            // Step 1: Basic enrichment
            setTimeout(() => setEnrichSteps(s => [
                { ...s[0], done: true },
                { text: 'Scraping website content...', done: false }
            ]), 800);

            setTimeout(() => setEnrichSteps(s => {
                if (s.length < 3) return [...s.map(x => ({ ...x, done: true })), { text: 'Analyzing with AI + website data...', done: false }];
                return s;
            }), 3000);

            const res = await axios.post(`${API}/enrichment/url`, { url }, { headers: getAuthHeader(), timeout: 60000 });
            const data = res.data?.data;
            const sources = data?._sources || [];
            const warnings = data?._warnings || [];

            // Show sources used
            const sourceLabels = sources.filter(s => s !== 'ai_knowledge').map(s => ({
                website_scrape: 'Website', exa: 'Exa', linkedin_apify: 'LinkedIn'
            }[s] || s));
            setEnrichSteps(s => [...s.map(x => ({ ...x, done: true })),
                { text: `Sources: ${sourceLabels.join(' + ')} + AI`, done: true },
                { text: 'Mapping company data...', done: false }
            ]);

            // Show warnings for disconnected/failed sources
            if (warnings.length > 0) {
                setTimeout(() => {
                    setEnrichSteps(s => [
                        ...s,
                        ...warnings.map(w => ({ text: `⚠ ${w.msg}`, done: true, warning: true }))
                    ]);
                }, 500);
            }

            if (data) {
                // Apply basic fields
                setForm(prev => {
                    const updated = { ...prev };
                    Object.entries(data).forEach(([key, val]) => {
                        if (val !== null && val !== undefined && val !== '' && !prev[key]) {
                            updated[key] = val;
                        }
                    });
                    return updated;
                });

                // Step 2: Deep research for description
                setEnrichSteps(s => [...s.map(x => ({ ...x, done: true })), { text: 'Conducting deep company research...', done: false }]);

                try {
                    const deepRes = await axios.post(`${API}/enrichment/url`, {
                        url,
                        deep: true,
                    }, { headers: getAuthHeader(), timeout: 45000 });
                    const deepData = deepRes.data?.data;
                    if (deepData?.description) {
                        setForm(prev => ({ ...prev, description: deepData.description }));
                    }
                } catch {}

                setEnrichSteps(s => [...s.map(x => ({ ...x, done: true })), { text: 'Research complete!', done: true }]);
                toast.success('AI enrichment complete — review the auto-filled fields');
            }
        } catch (err) {
            const detail = err.response?.data?.detail || 'Enrichment failed';
            const is429 = err.response?.status === 429 || detail.includes('quota') || detail.includes('429');
            if (is429) {
                toast.error('AI credits exhausted — check OpenAI billing at platform.openai.com', { duration: 8000 });
            } else {
                toast.error(detail);
            }
            setEnrichSteps(s => [...s.map(x => ({ ...x, done: true })), { text: is429 ? 'AI credits exhausted — add credits at platform.openai.com' : 'Research failed', done: true, error: true }]);
        } finally {
            setTimeout(() => { setEnriching(false); setEnrichSteps([]); }, 5000);
        }
    };

    const handleSave = async () => {
        if (!form.name) return toast.error('Company name is required');
        setLoading(true);
        // Clean empty strings from numeric fields to avoid Pydantic validation errors
        const payload = { ...form };
        ['annual_revenue', 'num_employees', 'founded_year'].forEach(k => {
            if (payload[k] === '' || payload[k] === null || payload[k] === undefined) delete payload[k];
        });
        try {
            let res;
            if (account) {
                res = await axios.put(`${API}/accounts/${account.account_id}`, payload, { headers: getAuthHeader() });
            } else {
                res = await axios.post(`${API}/accounts`, payload, { headers: getAuthHeader() });
            }
            toast.success(account ? 'Company updated' : 'Company created');
            onSave(res.data);
            onClose();
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (err.response?.status === 409 && detail?.code === 'duplicate_company') {
                setDuplicate(detail);
                toast.error(detail.allocated
                    ? `${detail.name} already exists and is allocated to ${detail.owner_name || 'another rep'}`
                    : `${detail.name} already exists — you can take ownership instead`);
            } else {
                toast.error('Failed to save company');
            }
        }
        finally { setLoading(false); }
    };

    const INPUT = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20";

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
                <DialogHeader className="px-6 pt-5 pb-3">
                    <DialogTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {account ? 'Edit Company' : 'New Company'}
                    </DialogTitle>
                </DialogHeader>

                <div className="px-6 pb-5 space-y-4">
                    {/* Duplicate company gate */}
                    {duplicate && (
                        <div className={`rounded-xl border p-4 ${duplicate.allocated ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                            <div className="flex items-start gap-2">
                                <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${duplicate.allocated ? 'text-red-500' : 'text-amber-500'}`} />
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-semibold ${duplicate.allocated ? 'text-red-700' : 'text-amber-700'}`}>
                                        {duplicate.name} already exists in the CRM
                                    </p>
                                    <p className={`text-xs mt-0.5 ${duplicate.allocated ? 'text-red-600' : 'text-amber-600'}`}>
                                        {duplicate.allocated_to_me
                                            ? 'This company is already allocated to you — open it to keep working on it.'
                                            : duplicate.allocated
                                                ? `This company is allocated to ${duplicate.owner_name || 'another rep'}. You can't proceed with a duplicate.`
                                                : 'This company is not allocated to anyone yet — you can take ownership instead of creating a duplicate.'}
                                    </p>
                                    <div className="flex gap-2 mt-2">
                                        <button onClick={() => { onClose(); navigate(`/companies/${duplicate.account_id}`); }}
                                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-stone-200 bg-white text-stone-700 hover:bg-stone-50">
                                            View company
                                        </button>
                                        {duplicate.claimable && (
                                            <button onClick={handleClaim} disabled={claiming}
                                                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50">
                                                {claiming && <Loader2 className="w-3 h-3 animate-spin" />}
                                                Take ownership
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Website URL + AI Enrich */}
                    <div className="bg-amber-50/50 border border-amber-100 rounded-xl p-4">
                        <label className="block text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1.5">Website URL — AI Auto-Fill</label>
                        <div className="flex gap-2">
                            <input value={form.website || ''} onChange={e => setForm(p => ({ ...p, website: e.target.value }))}
                                placeholder="https://company.com" className={`flex-1 ${INPUT}`} />
                            <button onClick={handleEnrich} disabled={enriching || !form.website}
                                className="flex items-center gap-1.5 px-5 py-2 text-sm font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40 transition-colors whitespace-nowrap shadow-sm">
                                {enriching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                {enriching ? 'Researching...' : 'AI Enrich'}
                            </button>
                        </div>
                        {!account && !enriching && <p className="text-[10px] text-amber-600/70 mt-1">Paste a URL and click AI Enrich to auto-fill all company details</p>}

                        {/* Enrichment progress */}
                        {enrichSteps.length > 0 && (
                            <div className="mt-3 space-y-1">
                                {enrichSteps.map((step, i) => (
                                    <div key={i} className={`flex items-center gap-2 text-xs ${
                                        step.error ? 'text-red-600' :
                                        step.warning ? 'text-amber-600' :
                                        step.done ? 'text-emerald-600' : 'text-amber-700'
                                    }`}>
                                        {step.warning ? (
                                            <Info className="w-3.5 h-3.5 flex-shrink-0" />
                                        ) : step.done ? (
                                            step.error ? <X className="w-3.5 h-3.5 flex-shrink-0" /> : <Check className="w-3.5 h-3.5 flex-shrink-0" />
                                        ) : (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
                                        )}
                                        <span>{step.text}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* 3-column grid for core fields */}
                    <div className="grid grid-cols-3 gap-3">
                        {[
                            { label: 'Company Name *', key: 'name', placeholder: 'Acme Corp' },
                            { label: 'Company Phone', key: 'phone', placeholder: '+1-555-0100' },
                            { label: 'Company Email', key: 'email', placeholder: 'info@acme.com', type: 'email' },
                            { label: 'Street Address', key: 'address', placeholder: '123 Main St' },
                            { label: 'City', key: 'city', placeholder: 'New York' },
                            { label: 'State / Province', key: 'state', placeholder: 'NY' },
                            { label: 'Country', key: 'country', placeholder: 'US' },
                            { label: 'Region', key: 'region', placeholder: 'North America' },
                            { label: 'Company HQ Country', key: 'hq_country', placeholder: 'United States' },
                            { label: 'Founded Year', key: 'founded_year', placeholder: '2020', type: 'number' },
                            { label: 'Funding Raised', key: 'funding_raised', placeholder: '$50M' },
                            { label: 'Annual Revenue', key: 'annual_revenue', placeholder: '1000000', type: 'number' },
                            { label: 'Employee Number Approx', key: 'num_employees', placeholder: '250', type: 'number' },
                            { label: 'Lead Investor', key: 'lead_investor', placeholder: 'Sequoia Capital' },
                        ].map(f => (
                            <div key={f.key}>
                                <label className="block text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-1">{f.label}</label>
                                <input type={f.type || 'text'} value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                                    placeholder={f.placeholder} className={INPUT} />
                            </div>
                        ))}
                        {/* Investor Type — dropdown */}
                        <div>
                            <label className="block text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Investor Type</label>
                            <select value={form.investor_type || ''} onChange={e => setForm(p => ({ ...p, investor_type: e.target.value }))}
                                className={`${INPUT} bg-white`}>
                                <option value="">Select...</option>
                                {INVESTOR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        {/* Employee Range keeps its slot */}
                        <div>
                            <label className="block text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Employee Range</label>
                            <select value={form.employee_range || ''} onChange={e => {
                                const sel = EMPLOYEE_RANGES.find(r => r.range === e.target.value);
                                setForm(p => ({ ...p, employee_range: e.target.value, size: sel ? sel.size.toLowerCase() : p.size }));
                            }} className={`${INPUT} bg-white`}>
                                <option value="">Select...</option>
                                {EMPLOYEE_RANGES.map(r => (
                                    <option key={r.range} value={r.range}>{r.category} — {r.range}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Category badge */}
                    {form.employee_range && (() => {
                        const sel = EMPLOYEE_RANGES.find(r => r.range === form.employee_range);
                        return sel ? (
                            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium ${sel.color}`}>
                                {sel.category} · {sel.size} · {sel.range} employees
                            </div>
                        ) : null;
                    })()}

                    {/* Social URLs — 2 columns */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Company LinkedIn URL</label>
                            <input value={form.linkedin_url || ''} onChange={e => setForm(p => ({ ...p, linkedin_url: e.target.value }))}
                                placeholder="https://linkedin.com/company/..." className={INPUT} />
                        </div>
                        <div>
                            <label className="block text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Twitter URL</label>
                            <input value={form.twitter_url || ''} onChange={e => setForm(p => ({ ...p, twitter_url: e.target.value }))}
                                placeholder="https://twitter.com/..." className={INPUT} />
                        </div>
                    </div>

                    {/* Industry + Size side by side */}
                    <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                            <IndustryMultiSelect
                                value={Array.isArray(form.industry) ? form.industry : (form.industry ? [form.industry] : [])}
                                onChange={v => setForm(p => ({ ...p, industry: v }))}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Company Size</label>
                            <select value={form.size} onChange={e => setForm(p => ({ ...p, size: e.target.value }))}
                                className={`${INPUT} bg-white capitalize`}>
                                {SIZES.map(s => <option key={s} className="capitalize">{s}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Status */}
                    <StatusSelector value={form.status} onChange={v => setForm(p => ({ ...p, status: v }))} />

                    {/* Description */}
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Description</label>
                        {form.description && !enriching ? (
                            <div className="border border-stone-200 rounded-lg overflow-hidden">
                                <div className="px-4 py-3 bg-stone-50/50 max-h-[200px] overflow-y-auto">
                                    {form.description.split('\n\n').map((para, i) => (
                                        <p key={i} className="text-sm text-stone-700 leading-relaxed mb-2 last:mb-0">{para}</p>
                                    ))}
                                </div>
                                <button type="button" onClick={() => {
                                    const el = document.getElementById('desc-edit');
                                    if (el) el.classList.toggle('hidden');
                                }} className="w-full px-4 py-1.5 text-[10px] text-stone-400 hover:text-stone-600 bg-white border-t border-stone-100 text-center">
                                    Click to edit
                                </button>
                                <textarea id="desc-edit" value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={8}
                                    className={`${INPUT} hidden rounded-none border-0 border-t border-stone-100`} />
                            </div>
                        ) : (
                            <textarea value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={6}
                                className={INPUT} placeholder="Company overview, products/services, market position, key differentiators..." />
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-2 border-t border-stone-100">
                        <button onClick={onClose} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                        <button onClick={handleSave} disabled={loading} className="px-5 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50 font-medium">
                            {loading ? 'Saving...' : account ? 'Update Company' : 'Create Company'}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Companies Page ─────────────────────────────────────────────────────────

export default function Accounts() {
    const { getAuthHeader, user } = useAuth();
    const { can } = usePermissions();
    const navigate = useNavigate();

    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterIndustry, setFilterIndustry] = useState('');
    const [filterOwner, setFilterOwner] = useState(user?.user_id || ''); // Default to current user
    const [showModal, setShowModal] = useState(false);
    const [editAccount, setEditAccount] = useState(null);
    const [total, setTotal] = useState(0);

    // Team assignment
    const [showAssign, setShowAssign] = useState(false);
    const [assignAccount, setAssignAccount] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [selectedMember, setSelectedMember] = useState('');

    // Selection & bulk delete
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [showBulkTagModal, setShowBulkTagModal] = useState(false);
    const [bulkTagAction, setBulkTagAction] = useState('add');
    const [bulkTags, setBulkTags] = useState([]);
    const [bulkTagging, setBulkTagging] = useState(false);

    // Deleted tab
    const [activeTab, setActiveTab] = useState('companies');
    const [deletedCompanies, setDeletedCompanies] = useState([]);
    const [deletedTotal, setDeletedTotal] = useState(0);
    const [restoring, setRestoring] = useState(false);

    // Table view management
    const {
        views, activeViewId, visibleColumnKeys, columnSizing, sorting, isDirty, loaded,
        switchView, saveView, saveAsView, deleteView, pushToOrg, addColumn, removeColumn,
        setSorting, setColumnSizing,
    } = useTableView('company', DEFAULT_COMPANY_COLUMNS);

    // Custom fields
    const { fields: customFields } = useCustomFields('account');

    // Build visible column configs
    const visibleColumns = buildVisibleColumns(COMPANY_FIELD_REGISTRY,
        visibleColumnKeys.map((key, idx) => ({ field_key: key, visible: true, order: idx, width: columnSizing[key] })),
        customFields
    );
    const allFields = getAllFields(COMPANY_FIELD_REGISTRY, customFields);
    const visibleKeySet = new Set(visibleColumnKeys);

    // Load companies
    const loadCompanies = useCallback(async (retryCount = 0) => {
        try {
            const params = new URLSearchParams();
            if (search) params.set('search', search);
            if (filterStatus) params.set('status', filterStatus);
            if (filterIndustry) params.set('industry', filterIndustry);
            if (filterOwner) params.set('owner_id', filterOwner);
            if (sorting[0]) {
                params.set('sort_by', sorting[0].id);
                params.set('sort_direction', sorting[0].desc ? 'desc' : 'asc');
            }
            params.set('page_size', '200');
            const res = await axios.get(`${API}/accounts?${params}`, { headers: getAuthHeader() });
            if (Array.isArray(res.data)) {
                setCompanies(res.data);
                setTotal(res.data.length);
            } else {
                setCompanies(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            console.error('Load companies error:', err?.response?.status, err?.message);
            // Auto-retry once on network error (CORS/abort during navigation)
            if (retryCount < 1 && (!err?.response || err?.message === 'Network Error')) {
                setTimeout(() => loadCompanies(retryCount + 1), 1000);
                return;
            }
            if (err?.response?.status === 401) {
                toast.error('Session expired — please refresh the page');
            }
            // Don't show error toast for network issues — will retry
        } finally { setLoading(false); }
    }, [search, filterStatus, filterIndustry, filterOwner, sorting, getAuthHeader]);

    useEffect(() => { loadCompanies(); }, [loadCompanies]);

    // Load team members for assignment
    useEffect(() => {
        const loadTeam = async () => {
            try {
                const res = await axios.get(`${API}/accounts/team-members`, { headers: getAuthHeader() });
                setTeamMembers(res.data || []);
            } catch { /* ignore */ }
        };
        loadTeam();
    }, [getAuthHeader]);

    const handleAssignMember = async (accountId, userId) => {
        try {
            await axios.put(`${API}/accounts/${accountId}/assign`, { add_member: userId }, { headers: getAuthHeader() });
            toast.success('Team member assigned');
            loadCompanies();
        } catch { toast.error('Failed to assign'); }
    };

    const handleRemoveMember = async (accountId, userId) => {
        try {
            await axios.put(`${API}/accounts/${accountId}/assign`, { remove_member: userId }, { headers: getAuthHeader() });
            toast.success('Team member removed');
            loadCompanies();
        } catch { toast.error('Failed to remove'); }
    };

    const handleSetOwner = async (accountId, userId) => {
        try {
            await axios.put(`${API}/accounts/${accountId}/assign`, { owner_id: userId }, { headers: getAuthHeader() });
            toast.success('Owner updated');
            loadCompanies();
        } catch { toast.error('Failed to update owner'); }
    };

    const handleSave = (saved) => {
        setCompanies(prev => {
            const idx = prev.findIndex(a => a.account_id === saved.account_id);
            if (idx >= 0) { const updated = [...prev]; updated[idx] = saved; return updated; }
            return [saved, ...prev];
        });
    };

    // Load deleted companies
    const loadDeletedCompanies = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/accounts/deleted`, { headers: getAuthHeader() });
            setDeletedCompanies(res.data.data || []);
            setDeletedTotal(res.data.total || 0);
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    useEffect(() => {
        if (activeTab === 'deleted') loadDeletedCompanies();
    }, [activeTab, loadDeletedCompanies]);

    const handleBulkDelete = async () => {
        if (deleteConfirmText !== 'DELETE') return;
        setDeleting(true);
        try {
            const ids = Array.from(selectedIds);
            await axios.delete(`${API}/accounts/bulk`, { data: { account_ids: ids }, headers: getAuthHeader() });
            toast.success(`${ids.length} ${ids.length > 1 ? 'companies' : 'company'} deleted`);
            setSelectedIds(new Set());
            setShowDeleteModal(false);
            setDeleteConfirmText('');
            loadCompanies();
        } catch { toast.error('Failed to delete companies'); }
        finally { setDeleting(false); }
    };

    const handleRestore = async (ids) => {
        const restoreIds = ids || Array.from(selectedIds);
        if (!restoreIds.length) return;
        setRestoring(true);
        try {
            await axios.put(`${API}/accounts/restore`, { account_ids: restoreIds }, { headers: getAuthHeader() });
            toast.success(`${restoreIds.length} ${restoreIds.length > 1 ? 'companies' : 'company'} restored`);
            setSelectedIds(new Set());
            loadDeletedCompanies();
            loadCompanies();
        } catch { toast.error('Failed to restore companies'); }
        finally { setRestoring(false); }
    };

    const handleBulkTag = async () => {
        if (!bulkTags.length) { toast.error('Please add at least one tag'); return; }
        setBulkTagging(true);
        try {
            const ids = Array.from(selectedIds);
            await axios.post(`${API}/accounts/bulk-tag`, {
                account_ids: ids,
                action: bulkTagAction,
                tags: bulkTags,
            }, { headers: getAuthHeader() });
            toast.success(`Tags ${bulkTagAction === 'add' ? 'added to' : 'removed from'} ${ids.length} companies`);
            setShowBulkTagModal(false);
            setBulkTags([]);
            setSelectedIds(new Set());
            loadAccounts();
        } catch { toast.error('Failed to update tags'); }
        finally { setBulkTagging(false); }
    };

    const isAdmin = user?.is_admin || user?.is_super_admin;

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Companies</h1>
                    <div className="flex items-center gap-2 mt-1">
                        <Building2 className="w-3.5 h-3.5 text-stone-400" />
                        <span className="text-sm text-stone-500" data-field="account.count">{activeTab === 'deleted' ? `${deletedTotal} deleted` : `${total} companies`}</span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {selectedIds.size > 0 && activeTab === 'companies' && (
                        <>
                        <button onClick={() => { setBulkTags([]); setBulkTagAction('add'); setShowBulkTagModal(true); }}
                            className="flex items-center gap-2 px-4 py-2 bg-stone-700 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors">
                            <Tag className="w-4 h-4" /> Tag ({selectedIds.size})
                        </button>
                        <button onClick={() => { setDeleteConfirmText(''); setShowDeleteModal(true); }}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors">
                            <Trash2 className="w-4 h-4" /> Delete ({selectedIds.size})
                        </button>
                        </>
                    )}
                    {selectedIds.size > 0 && activeTab === 'deleted' && (
                        <button onClick={() => handleRestore()} disabled={restoring}
                            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50">
                            <RotateCcw className="w-4 h-4" /> Restore ({selectedIds.size})
                        </button>
                    )}
                    {isAdmin && (
                        <button onClick={() => { setActiveTab(activeTab === 'deleted' ? 'companies' : 'deleted'); setSelectedIds(new Set()); }}
                            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                activeTab === 'deleted' ? 'bg-red-50 text-red-700 border border-red-200' : 'text-stone-500 hover:text-stone-700 hover:bg-stone-100'
                            }`}>
                            <Trash2 className="w-3.5 h-3.5" /> Deleted
                        </button>
                    )}
                    <button onClick={() => { setEditAccount(null); setShowModal(true); }}
                        className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors">
                        <Plus className="w-4 h-4" /> New Company
                    </button>
                </div>
            </div>

            {/* Toolbar: View Selector + Filters + Add Column */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 flex-1 flex-wrap">
                    <ViewSelector
                        views={views}
                        activeViewId={activeViewId}
                        isDirty={isDirty}
                        onSwitchView={switchView}
                        onSaveView={saveView}
                        onSaveAsView={saveAsView}
                        onDeleteView={deleteView}
                        onPushToOrg={pushToOrg}
                        isAdmin={isAdmin}
                    />
                    <div className="w-px h-6 bg-stone-200" />
                    <div className="relative flex-1 max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input type="text" placeholder="Search companies..." value={search} onChange={e => setSearch(e.target.value)}
                            className="w-full pl-9 pr-3 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </div>
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                        className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none capitalize">
                        <option value="">All Status</option>
                        {STATUSES.map(s => <option key={s.value} value={s.value} className="capitalize">{s.label}</option>)}
                    </select>
                    <select value={filterIndustry} onChange={e => setFilterIndustry(e.target.value)}
                        className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                        <option value="">All Industries</option>
                        {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
                    </select>
                    <select value={filterOwner} onChange={e => setFilterOwner(e.target.value)}
                        className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                        <option value="">All Owners</option>
                        {teamMembers.map(m => (
                            <option key={m.user_id} value={m.user_id}>{m.name}</option>
                        ))}
                    </select>
                </div>
                <AddColumnPanel
                    allFields={allFields}
                    visibleKeys={visibleKeySet}
                    onAddColumn={addColumn}
                />
            </div>

            {/* Data Table */}
            {activeTab === 'companies' ? (
                loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                    </div>
                ) : (<>
                    <DataTable
                        data={companies}
                        visibleColumns={visibleColumns}
                        sorting={sorting}
                        onSortingChange={setSorting}
                        columnSizing={columnSizing}
                        onColumnSizingChange={setColumnSizing}
                        onRowClick={(row) => navigate(`/companies/${row.account_id}`)}
                        selectable
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                        idField="account_id"
                    />
                    {/* Admin: quick assign buttons */}
                    {isAdmin && companies.length > 0 && (
                        <div className="flex items-center gap-2 mt-2 text-xs text-stone-400">
                            <UserPlus className="w-3.5 h-3.5" />
                            <span>Click a company row, then use the "Assign Team" button on the detail page, or:</span>
                            {companies.filter(c => !c.team_members?.length && !c.owner_name).slice(0, 3).map(c => (
                                <button key={c.account_id} onClick={(e) => { e.stopPropagation(); setAssignAccount(c); setShowAssign(true); }}
                                    className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
                                    Assign: {c.name}
                                </button>
                            ))}
                        </div>
                    )}
                </>)
            ) : (
                /* Deleted companies table */
                <div className="overflow-auto bg-white rounded-xl border border-stone-200">
                    <table className="w-full">
                        <thead className="sticky top-0 z-10 bg-stone-50 border-b border-stone-200">
                            <tr>
                                <th className="px-3 py-2.5 text-left w-10">
                                    <input type="checkbox"
                                        checked={deletedCompanies.length > 0 && deletedCompanies.every(c => selectedIds.has(c.account_id))}
                                        ref={el => { if (el) el.indeterminate = deletedCompanies.some(c => selectedIds.has(c.account_id)) && !deletedCompanies.every(c => selectedIds.has(c.account_id)); }}
                                        onChange={(e) => {
                                            if (e.target.checked) setSelectedIds(new Set(deletedCompanies.map(c => c.account_id)));
                                            else setSelectedIds(new Set());
                                        }}
                                        className="w-4 h-4 rounded border-stone-300 cursor-pointer" />
                                </th>
                                {['Company', 'Industry', 'Status', 'Deleted By', 'Deleted At', ''].map(h => (
                                    <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {deletedCompanies.length === 0 ? (
                                <tr><td colSpan={7} className="text-center py-16 text-sm text-stone-400">No deleted companies</td></tr>
                            ) : deletedCompanies.map(c => (
                                <tr key={c.account_id} className="border-b border-stone-100 hover:bg-stone-50">
                                    <td className="px-3 py-2.5">
                                        <input type="checkbox" checked={selectedIds.has(c.account_id)}
                                            onChange={(e) => {
                                                const next = new Set(selectedIds);
                                                if (e.target.checked) next.add(c.account_id);
                                                else next.delete(c.account_id);
                                                setSelectedIds(next);
                                            }}
                                            className="w-4 h-4 rounded border-stone-300 cursor-pointer" />
                                    </td>
                                    <td className="px-3 py-2.5 text-sm font-medium text-stone-900">{c.name}</td>
                                    <td className="px-3 py-2.5 text-sm text-stone-600">{c.industry || '—'}</td>
                                    <td className="px-3 py-2.5 text-sm text-stone-600 capitalize">{c.status || '—'}</td>
                                    <td className="px-3 py-2.5 text-sm text-stone-500">{c.deleted_by_name || '—'}</td>
                                    <td className="px-3 py-2.5 text-sm text-stone-500">{c.deleted_at ? new Date(c.deleted_at).toLocaleDateString() : '—'}</td>
                                    <td className="px-3 py-2.5">
                                        <button onClick={() => handleRestore([c.account_id])}
                                            className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
                                            <RotateCcw className="w-3 h-3" /> Restore
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Delete {selectedIds.size} {selectedIds.size > 1 ? 'Companies' : 'Company'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <p className="text-sm text-stone-600">
                            This will soft-delete the selected {selectedIds.size > 1 ? 'companies' : 'company'}. They can be restored from the "Deleted" tab.
                        </p>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">
                                Type DELETE to confirm
                            </label>
                            <input type="text" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
                                placeholder="DELETE" autoFocus
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400" />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                            <button onClick={handleBulkDelete} disabled={deleteConfirmText !== 'DELETE' || deleting}
                                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Bulk Tag Modal */}
            <Dialog open={showBulkTagModal} onOpenChange={setShowBulkTagModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-stone-900">
                            <Tag className="w-5 h-5" />
                            Tag {selectedIds.size} Compan{selectedIds.size > 1 ? 'ies' : 'y'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Action</label>
                            <select value={bulkTagAction} onChange={e => setBulkTagAction(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                <option value="add">Add Tags</option>
                                <option value="remove">Remove Tags</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Tags</label>
                            <TagInput
                                value={bulkTags}
                                onChange={setBulkTags}
                                placeholder={bulkTagAction === 'add' ? 'Add tags...' : 'Tags to remove...'}
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setShowBulkTagModal(false)}
                                className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                            <button onClick={handleBulkTag} disabled={!bulkTags.length || bulkTagging}
                                className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed">
                                {bulkTagging ? 'Updating...' : `${bulkTagAction === 'add' ? 'Add' : 'Remove'} Tags`}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Company Modal */}
            <CompanyModal
                open={showModal}
                onClose={() => { setShowModal(false); setEditAccount(null); }}
                account={editAccount}
                onSave={handleSave}
            />

            {/* Team Assignment Dialog */}
            <Dialog open={showAssign} onOpenChange={setShowAssign}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-stone-700" />
                            Assign Team — {assignAccount?.name}
                        </DialogTitle>
                    </DialogHeader>
                    {assignAccount && (
                        <div className="space-y-4 py-2">
                            {/* Current Owner */}
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Owner</label>
                                <select
                                    value={assignAccount.owner_id || ''}
                                    onChange={e => { handleSetOwner(assignAccount.account_id, e.target.value); setAssignAccount(prev => ({ ...prev, owner_id: e.target.value })); }}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                >
                                    <option value="">Unassigned</option>
                                    {teamMembers.map(m => (
                                        <option key={m.user_id} value={m.user_id}>{m.name} ({m.role})</option>
                                    ))}
                                </select>
                            </div>

                            {/* Current Team */}
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                                    Team Members ({assignAccount.team_members?.length || 0})
                                </label>
                                <div className="space-y-1.5 mb-3">
                                    {(assignAccount.team_members || []).map(member => (
                                        <div key={member.user_id} className="flex items-center justify-between px-3 py-2 bg-stone-50 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full bg-stone-200 flex items-center justify-center text-[10px] font-bold text-stone-600">
                                                    {member.name?.charAt(0)?.toUpperCase()}
                                                </div>
                                                <span className="text-sm text-stone-700">{member.name}</span>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    handleRemoveMember(assignAccount.account_id, member.user_id);
                                                    setAssignAccount(prev => ({
                                                        ...prev,
                                                        team_members: prev.team_members.filter(m => m.user_id !== member.user_id)
                                                    }));
                                                }}
                                                className="p-1 text-stone-400 hover:text-red-500 transition-colors"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                    {(!assignAccount.team_members || assignAccount.team_members.length === 0) && (
                                        <p className="text-xs text-stone-400 text-center py-2">No team members assigned</p>
                                    )}
                                </div>

                                {/* Add member */}
                                <div className="flex items-center gap-2">
                                    <select
                                        value={selectedMember}
                                        onChange={e => setSelectedMember(e.target.value)}
                                        className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                    >
                                        <option value="">Select team member...</option>
                                        {teamMembers
                                            .filter(m => !(assignAccount.team_members || []).some(tm => tm.user_id === m.user_id))
                                            .map(m => (
                                                <option key={m.user_id} value={m.user_id}>{m.name} ({m.role})</option>
                                            ))
                                        }
                                    </select>
                                    <Button size="sm" disabled={!selectedMember} onClick={() => {
                                        if (selectedMember) {
                                            handleAssignMember(assignAccount.account_id, selectedMember);
                                            const member = teamMembers.find(m => m.user_id === selectedMember);
                                            setAssignAccount(prev => ({
                                                ...prev,
                                                team_members: [...(prev.team_members || []), { user_id: selectedMember, name: member?.name || '' }]
                                            }));
                                            setSelectedMember('');
                                        }
                                    }}>
                                        <UserPlus className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
